import type { useI18n } from '@hive/i18n';
import { track } from '@hive/track';
import type { DocMode } from '@blocksuite/hive/model';
import { ImportIcon, PlusIcon } from '@blocksuite/icons/rc';

import type { usePageHelper } from '../blocksuite/block-suite-page-list/utils';
import type { GlobalDialogService } from '../modules/dialogs';
import { registerHiveCommand } from './registry';

export function registerHiveCreationCommands({
  pageHelper,
  t,
  globalDialogService,
}: {
  t: ReturnType<typeof useI18n>;
  pageHelper: ReturnType<typeof usePageHelper>;
  globalDialogService: GlobalDialogService;
}) {
  const unsubs: Array<() => void> = [];
  unsubs.push(
    registerHiveCommand({
      id: 'hive:new-page',
      category: 'hive:creation',
      label: t['com.hive.cmdk.hive.new-page'](),
      icon: <PlusIcon />,
      keyBinding: BUILD_CONFIG.isElectron
        ? {
            binding: '$mod+N',
            skipRegister: true,
          }
        : undefined,
      run() {
        track.$.cmdk.creation.createDoc({ mode: 'page' });

        pageHelper.createPage('page' as DocMode);
      },
    })
  );

  unsubs.push(
    registerHiveCommand({
      id: 'hive:new-edgeless-page',
      category: 'hive:creation',
      icon: <PlusIcon />,
      label: t['com.hive.cmdk.hive.new-edgeless-page'](),
      run() {
        track.$.cmdk.creation.createDoc({
          mode: 'edgeless',
        });

        pageHelper.createEdgeless();
      },
    })
  );

  unsubs.push(
    registerHiveCommand({
      id: 'hive:new-workspace',
      category: 'hive:creation',
      icon: <PlusIcon />,
      label: t['com.hive.cmdk.hive.new-workspace'](),
      run() {
        track.$.cmdk.workspace.createWorkspace();

        globalDialogService.open('create-workspace', {});
      },
    })
  );
  unsubs.push(
    registerHiveCommand({
      id: 'hive:import-workspace',
      category: 'hive:creation',
      icon: <ImportIcon />,
      label: t['com.hive.cmdk.hive.import-workspace'](),
      preconditionStrategy: () => {
        return BUILD_CONFIG.isElectron;
      },
      run() {
        track.$.cmdk.workspace.createWorkspace({
          control: 'import',
        });

        globalDialogService.open('import-workspace', undefined);
      },
    })
  );

  return () => {
    unsubs.forEach(unsub => unsub());
  };
}
